import type { EditableCallbackParams, RowClassParams } from 'ag-grid-community'
import { cx } from 'class-variance-authority'
import type { Review } from '@skop-app/lib-domain/types'
import type { GridRulesConfig } from '../types'
import { isRecord } from '@/utils/typeGuards'
import { resolveReferenceAccessFromContext } from '@/features/references/reference.access'
import {
  getReviewGridProjectionFromContext,
  isDisplayablePendingReviewThread,
} from '@/utils/reviews'

type RowReview = Pick<Review, 'status' | 'messages'>

type RowReviewMap = Record<string, RowReview[]>

type RowWithId = {
  id?: string
}

type BaseAuthInput = {
  context: unknown
  organizationId?: string
  byUserID?: string
}

type BaseAuthResult = {
  isGuest: boolean
  isExternal: boolean
  canCreate: boolean
  canUpdate: boolean
}

export const checkReferencesAuth = ({
  context,
  organizationId,
  byUserID,
}: BaseAuthInput): BaseAuthResult => {
  const contextRecord = isRecord(context) ? context : {}
  const authorization = isRecord(contextRecord.authorization)
    ? contextRecord.authorization
    : {}
  const referencesAuth = isRecord(authorization.referencesAuth)
    ? authorization.referencesAuth
    : {}
  const authorizationUser = isRecord(authorization.user)
    ? authorization.user
    : {}
  const row = {
    ...(typeof organizationId === 'string'
      ? { organizationID: organizationId }
      : {}),
    ...(typeof byUserID === 'string' ? { byUserID } : {}),
  }
  const access = resolveReferenceAccessFromContext({
    context,
    row,
  })

  return {
    isGuest: authorizationUser.role === 'GUEST',
    canCreate: Boolean(referencesAuth.canCreate) && access.canCreateReference,
    canUpdate: Boolean(referencesAuth.canUpdate),
    isExternal: !access.canEditRow,
  }
}

const toRowReviewMap = (value: unknown): RowReviewMap => {
  if (!isRecord(value)) return {}
  const result: RowReviewMap = {}
  for (const [key, entry] of Object.entries(value)) {
    if (!Array.isArray(entry)) continue
    const normalized = entry
      .filter((item) => isRecord(item))
      .map((item) => {
        const status = item.status
        const messages = item.messages
        return typeof status === 'string' ? { status, messages } : null
      })
      .filter((review): review is RowReview => review !== null)
    if (normalized.length > 0) result[key] = normalized
  }
  return result
}

const getReviewsById = (context: unknown): RowReviewMap => {
  return toRowReviewMap(
    getReviewGridProjectionFromContext(context).reviewsByElementId,
  )
}

const toRowData = (value: unknown): RowWithId => {
  if (!isRecord(value)) return {}
  return {
    id: typeof value.id === 'string' ? value.id : undefined,
  }
}

const hasPendingReview = (
  data: RowWithId,
  reviewsById: RowReviewMap,
): boolean => {
  const id = data.id
  if (!id) return false
  return (
    reviewsById[id]?.some((review) =>
      isDisplayablePendingReviewThread(review),
    ) ?? false
  )
}

const hasPendingReviewInNode = <Row>(
  params: RowClassParams<Row>,
  reviewsById: RowReviewMap,
): boolean => {
  const { node } = params
  return hasPendingReview(toRowData(node.data), reviewsById)
}

const hasPendingReviewInDescendants = <Row>(
  params: RowClassParams<Row>,
  reviewsById: RowReviewMap,
): boolean => {
  const { node } = params
  if (!node.group) return false
  return (
    node.allLeafChildren?.some((child) =>
      hasPendingReview(toRowData(child.data), reviewsById),
    ) ?? false
  )
}

export const createRowClassFn = <Row>(rules?: GridRulesConfig<Row>) => {
  return (params: RowClassParams<Row>) => {
    const reviewsById = getReviewsById(params.context)
    const hasReviewedDescendants =
      rules?.reviewHighlight !== false &&
      hasPendingReviewInDescendants(params, reviewsById)
    const inReview =
      rules?.reviewHighlight !== false &&
      hasPendingReviewInNode(params, reviewsById)
    const isDisabled = rules?.isRowDisabled
      ? rules.isRowDisabled(params)
      : rules?.isRowEditable
        ? !rules.isRowEditable(
            params as EditableCallbackParams<Row> | RowClassParams<Row>,
          )
        : false
    const rowData = toRowData(params.node.data)

    return cx({
      'ag-row-with-review': inReview,
      'bg-orange-500/25': inReview,
      'ag-row-with-review-descendants': hasReviewedDescendants,
      'font-italic text-slate-200': rowData.id?.startsWith('new'),
      'ag-row-disabled': isDisabled,
    })
  }
}

export const rowClass = createRowClassFn()
